import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import toast from 'react-hot-toast';
import {
  Home,
  CreditCard,
  Package,
  UserCheck,
  Users,
  ShoppingCart,
  Calculator,
  BarChart3,
  X,
  Eye,
  Edit,
  Trash2,
  Plus,
  ChevronDown,
  ChevronRight,
  Calendar,
  DollarSign,
  Filter,
  Search,
  ArrowLeft,
  ArrowRight
} from 'lucide-react';

const CashAnalysis = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Modal states
  const [showOpeningBalanceModal, setShowOpeningBalanceModal] = useState(false);

  // Cash tracking states - initialized with zeros, will be populated from API
  const [salePos, setSalePos] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });
  const [cashConversion, setCashConversion] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });
  const [additionalCash, setAdditionalCash] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });
  const [convertCashToEcash, setConvertCashToEcash] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });
  const [convertEcashToCash, setConvertEcashToCash] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });
  const [bankLodgement, setBankLodgement] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });
  const [totalCashProceeds, setTotalCashProceeds] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });
  const [totalCashPayments, setTotalCashPayments] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });
  const [previousBalance, setPreviousBalance] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });
  const [totalCashAvailable, setTotalCashAvailable] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });


  const [totalCashReceived, setTotalCashReceived] = useState({
    200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0
  });

  // Modal states
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Transaction form state
  const [transactionForm, setTransactionForm] = useState({
    cardType: '',
    denominations: { 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0 }
  });

  // Opening balance form state
  const [openingBalanceForm, setOpeningBalanceForm] = useState({
    denominations: { 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0 }
  });


  // API states
  const [analysisData, setAnalysisData] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Dynamic reconciliation and summary data
  const [reconciliationData, setReconciliationData] = useState({
    increase_coins: 0,
    momo_reversal: 0,
    momo_sales: 0,
    total_cash_sales: 0,
    next_day_amount: 0
  });
  
  const [summaryData, setSummaryData] = useState({
    momo_balance: 0,
    cash_total: 0,
    sub_total: 0,
    bank_balance: 0,
    grand_total: 0
  });

  // Mobile money balance state
  const [mobileMoneyBalance, setMobileMoneyBalance] = useState(0);
  
  // Mobile money sales state
  const [mobileMoneySales, setMobileMoneySales] = useState({
    total_amount: 0,
    transaction_count: 0,
    sales: []
  });

  // Mobile money expenses state
  const [mobileMoneyExpenses, setMobileMoneyExpenses] = useState({
    total_amount: 0,
    transaction_count: 0,
    expenses: []
  });

  // Bank transfer state
  const [bankTransfers, setBankTransfers] = useState({
    received: { total_amount: 0, transaction_count: 0, transactions: [] },
    paid: { total_amount: 0, transaction_count: 0, transactions: [] },
    lodgements: { total_amount: 0, transaction_count: 0, transactions: [] },
    previous_balance: 0
  });
  

  const denominations = [200, 100, 50, 20, 10, 5, 2, 1, 0.5, 0.2, 0.1, 0.05];


  // Helper function to calculate total value from denomination counts
  const calculateTotalValue = (denominations) => {
    return Object.entries(denominations).reduce((total, [denomination, quantity]) => {
      return total + (parseFloat(denomination) * (parseFloat(quantity) || 0));
    }, 0);
  };

  // Helper function to format currency consistently
  const formatCurrency = (amount) => {
    return `Ghs ${parseFloat(amount || 0).toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Helper function to update denomination values
  const updateDenomination = (setter, denomination, value) => {
    setter(prev => ({
      ...prev,
      [denomination]: parseInt(value) || 0
    }));
  };

  // Helper function to get previous business day based on selected date
  const getPreviousBusinessDay = () => {
    const selectedDate = new Date(currentDate);
    let previousDay = new Date(selectedDate);
    
    if (selectedDate.getDay() === 1) { // Monday
      previousDay.setDate(selectedDate.getDate() - 2); // Saturday (skip Sunday only)
    } else {
      previousDay.setDate(selectedDate.getDate() - 1); // Regular day minus 1
    }
    
    return previousDay.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };


  // Modal handlers
  const handleOpenAddAdjustmentModal = () => {
    setShowAdjustmentModal(true);
  };

  const handleOpenTransactionModal = () => {
    setTransactionForm({
      cardType: '',
      denominations: {}
    });
    setShowTransactionModal(true);
  };

  const handleCloseTransactionModal = () => {
    setShowTransactionModal(false);
    setTransactionForm({
      cardType: '',
      denominations: {}
    });
  };

  const handleOpenOpeningBalanceModal = () => {
    setShowOpeningBalanceModal(true);
  };

  const handleCloseOpeningBalanceModal = () => {
    setShowOpeningBalanceModal(false);
    setOpeningBalanceForm({
      denominations: { 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0 }
    });
  };

  const handleTransactionFormChange = (field, value) => {
    setTransactionForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDenominationChange = (denomination, value) => {
    setTransactionForm(prev => ({
      ...prev,
      denominations: {
        ...prev.denominations,
        [denomination]: parseInt(value) || 0
      }
    }));
  };

  const handleOpeningBalanceDenominationChange = (denomination, value) => {
    setOpeningBalanceForm(prev => ({
      ...prev,
      denominations: {
        ...prev.denominations,
        [denomination]: parseInt(value) || 0
      }
    }));
  };

  const handleReconciliationChange = (field, value) => {
    setReconciliationData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  const handleSaveTransaction = async () => {
    try {
      
      // Save all denominations for the selected card type
      const promises = Object.entries(transactionForm.denominations).map(([denomination, quantity]) => 
        apiService.updateCashDenomination({
          date: currentDate,
          type: transactionForm.cardType,
          value: denomination,
          quantity: quantity
        })
      );
      
      await Promise.all(promises);
      
      // If we're updating any of the source values, also save the calculated values
      const proceedsSources = ['sale_pos', 'additional_cash', 'convert_ecash_to_cash', 'total_cash_received'];
      const netFlowSources = ['total_cash_received', 'total_cash_proceeds', 'total_cash_payments', 'convert_cash_to_ecash'];
      const availableSources = ['previous_balance', 'bank_lodgement'];
      
      // Save calculated Total Cash Proceeds
      if (proceedsSources.includes(transactionForm.cardType)) {
        const calculatedProceeds = calculateTotalCashProceeds;
        const proceedsPromises = Object.entries(calculatedProceeds).map(([denomination, quantity]) => 
          apiService.updateCashDenomination({
            date: currentDate,
            type: 'total_cash_proceeds',
            value: denomination,
            quantity: quantity
          })
        );
        await Promise.all(proceedsPromises);
      }
      
      // Save calculated Total Cash Available (if any relevant source changed)
      if (proceedsSources.includes(transactionForm.cardType) || 
          netFlowSources.includes(transactionForm.cardType) || 
          availableSources.includes(transactionForm.cardType)) {
        const calculatedAvailable = calculateTotalCashAvailable;
        const availablePromises = Object.entries(calculatedAvailable).map(([denomination, quantity]) => 
          apiService.updateCashDenomination({
            date: currentDate,
            type: 'total_cash_available',
            value: denomination,
            quantity: quantity
          })
        );
        await Promise.all(availablePromises);
      }
      
      // If this is a cash conversion, also record it in mobile money tracking
      if (transactionForm.cardType === 'convert_cash_to_ecash') {
        const totalAmount = Object.entries(transactionForm.denominations).reduce((sum, [denomination, quantity]) => {
          return sum + (parseInt(denomination) * (quantity || 0));
        }, 0);
        
        if (totalAmount > 0) {
          await apiService.recordCashToMobileMoney(
            totalAmount,
            `CASH-MOMO-${Date.now()}`,
            `Cash to mobile money conversion - ${currentDate}`
          );
        }
      } else if (transactionForm.cardType === 'convert_ecash_to_cash') {
        const totalAmount = Object.entries(transactionForm.denominations).reduce((sum, [denomination, quantity]) => {
          return sum + (parseInt(denomination) * (quantity || 0));
        }, 0);
        
        if (totalAmount > 0) {
          await apiService.recordMobileMoneyToCash(
            totalAmount,
            `MOMO-CASH-${Date.now()}`,
            `Mobile money to cash conversion - ${currentDate}`
          );
        }
      }
      
      // Silent success - no toast notification
      
      // Refresh both cash analysis and mobile money balance
      await Promise.all([
        fetchCashAnalysis(currentDate),
        fetchMobileMoneyBalance()
      ]);
      
        // Reset form after successful transaction
        setTransactionForm({
          cardType: '',
          denominations: { 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0 }
        });
      
      // Close modal
      handleCloseTransactionModal();
      
    } catch (error) {
      console.error('Error saving transaction:', error);
      // Silent error - no toast notification
    }
  };

  const handleSaveOpeningBalance = async () => {
    try {
      // Save all denominations for the previous balance
      const promises = Object.entries(openingBalanceForm.denominations).map(([denomination, quantity]) => 
        apiService.updateCashDenomination({
          date: currentDate,
          type: 'previous_balance',
          value: denomination,
          quantity: quantity
        })
      );
      
      await Promise.all(promises);
      
      // Refresh cash analysis data
      await fetchCashAnalysis(currentDate);
      
      // Close modal and reset form
      handleCloseOpeningBalanceModal();
      
    } catch (error) {
      console.error('Error saving opening balance:', error);
      // Silent error - no toast notification
    }
  };

  // Helper function to deep compare objects
  const deepEqual = useCallback((obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (obj1 == null || obj2 == null) return false;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object') return obj1 === obj2;
    
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    
    if (keys1.length !== keys2.length) return false;
    
    for (let key of keys1) {
      if (!keys2.includes(key)) return false;
      if (!deepEqual(obj1[key], obj2[key])) return false;
    }
    
    return true;
  }, []);

  // API Functions
  const fetchCashAnalysis = useCallback(async (date) => {
    try {
      const response = await apiService.getCashAnalysis(date);
      
      if (response.success) {
        setAnalysisData(response.data);
        
        // Update state with fetched data
        if (response.data.denominations) {
          const denoms = response.data.denominations;
          
          // Update all denomination states with deep comparison to prevent unnecessary re-renders
          const defaultDenoms = { 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0 };
          
          setPreviousBalance(prev => {
            const newData = denoms.previous_balance || defaultDenoms;
            return deepEqual(prev, newData) ? prev : newData;
          });
          setSalePos(prev => {
            const newData = denoms.sale_pos || defaultDenoms;
            return deepEqual(prev, newData) ? prev : newData;
          });
          setAdditionalCash(prev => {
            const newData = denoms.additional_cash || defaultDenoms;
            return deepEqual(prev, newData) ? prev : newData;
          });
          setCashConversion(prev => {
            const newData = denoms.cash_conversion || defaultDenoms;
            return deepEqual(prev, newData) ? prev : newData;
          });
          setConvertEcashToCash(prev => {
            const newData = denoms.convert_ecash_to_cash || defaultDenoms;
            return deepEqual(prev, newData) ? prev : newData;
          });
          setConvertCashToEcash(prev => {
            const newData = denoms.convert_cash_to_ecash || defaultDenoms;
            return deepEqual(prev, newData) ? prev : newData;
          });
          setBankLodgement(prev => {
            const newData = denoms.bank_lodgement || defaultDenoms;
            return deepEqual(prev, newData) ? prev : newData;
          });
          // Don't set totalCashReceived from backend here - it will be fetched separately
          // This prevents conflicting data sources
          // setTotalCashReceived(prev => {
          //   const newData = denoms.total_cash_received || defaultDenoms;
          //   return deepEqual(prev, newData) ? prev : newData;
          // });
          // Don't set totalCashPayments from backend here - it will be fetched separately
          // This prevents conflicting data sources
          // setTotalCashPayments(prev => {
          //   const newData = denoms.total_cash_payments || defaultDenoms;
          //   return deepEqual(prev, newData) ? prev : newData;
          // });
        }
        
        // Update reconciliation data if available
        if (response.data.reconciliation) {
          setReconciliationData({
            increase_coins: parseFloat(response.data.reconciliation.increase_coins) || 0,
            momo_reversal: parseFloat(response.data.reconciliation.momo_reversal) || 0,
            momo_sales: parseFloat(response.data.reconciliation.momo_sales) || 0,
            total_cash_sales: parseFloat(response.data.reconciliation.total_cash_sales) || 0,
            next_day_amount: parseFloat(response.data.reconciliation.next_day_amount) || 0
          });
        }
        
        // Update summary data from analysis
        if (response.data.analysis) {
          setSummaryData({
            momo_balance: parseFloat(response.data.analysis.momo_balance) || 0,
            cash_total: parseFloat(response.data.analysis.cash_total) || 0,
            sub_total: parseFloat(response.data.analysis.sub_total) || 0,
            bank_balance: parseFloat(response.data.analysis.bank_balance) || 0,
            grand_total: parseFloat(response.data.analysis.grand_total) || 0
          });
        }
        
        // Silent update - no toast notification
      }
    } catch (error) {
      console.error('Error fetching cash analysis:', error);
      // Silent error - no toast notification
    }
  }, [deepEqual]); // Include deepEqual in dependencies

  // Fetch mobile money balance
  const fetchMobileMoneyBalance = useCallback(async () => {
    try {
      const response = await apiService.getMobileMoneyBalance();
      if (response.success) {
        setMobileMoneyBalance(parseFloat(response.data.current_balance) || 0);
      }
    } catch (error) {
      // Silent error - mobile money account may not be configured
      setMobileMoneyBalance(0);
    }
  }, []);

  // Fetch mobile money sales
  const fetchMobileMoneySales = useCallback(async () => {
    try {
      const response = await apiService.getMobileMoneySales(currentDate);
      if (response.success) {
        setMobileMoneySales({
          total_amount: parseFloat(response.data.total_amount) || 0,
          transaction_count: response.data.transaction_count || 0,
          sales: response.data.sales || []
        });
      }
    } catch (error) {
      console.error('Error fetching mobile money sales:', error);
      // Silent error - no toast notification
    }
  }, [currentDate]);

  // Fetch mobile money expenses
  const fetchMobileMoneyExpenses = useCallback(async () => {
    try {
      const expenseResponse = await apiService.getExpenses({ per_page: 1000 });
      const allExpenses = expenseResponse.data || [];
      const todayExpenses = allExpenses.filter(expense => {
        const expenseDate = expense.expense_date.includes('T') 
          ? expense.expense_date.split('T')[0] 
          : expense.expense_date;
        return expenseDate === currentDate && 
               expense.mode_of_payment && 
               expense.mode_of_payment.toLowerCase() === 'mobile_money';
      });

      const totalAmount = todayExpenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0);
      
      setMobileMoneyExpenses({
        total_amount: totalAmount,
        transaction_count: todayExpenses.length,
        expenses: todayExpenses
      });
    } catch (error) {
      console.error('Error fetching mobile money expenses:', error);
      // Silent error - no toast notification
    }
  }, [currentDate]);

  // Fetch bank transfer data
  const fetchBankTransfers = useCallback(async () => {
    try {
      
      // Get yesterday's date for previous balance
      const currentDateObj = new Date(currentDate);
      const yesterday = new Date(currentDateObj);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      // Fetch yesterday's bank balance (this would need to be stored/calculated)
      // For now, we'll assume it's 0 or get it from a previous calculation
      let previousBankBalance = 0;
      
      // Fetch bank transfers received today (from customer payments)
      let bankTransfersReceived = [];
      try {
        // First try the general payments endpoint
        const paymentsResponse = await apiService.getPayments({
          start_date: currentDate,
          end_date: currentDate,
          per_page: 1000
        });
        if (paymentsResponse.success && paymentsResponse.data && Array.isArray(paymentsResponse.data)) {
          bankTransfersReceived = paymentsResponse.data.filter(payment => 
            payment.payment_method && payment.payment_method.toLowerCase() === 'bank_transfer'
          );
        } else {
        }
        
        // Also try sales report as backup
        const salesResponse = await apiService.getSalesReport(currentDate, currentDate);
        if (salesResponse.success && salesResponse.sales && Array.isArray(salesResponse.sales)) {
          const salesBankTransfers = salesResponse.sales.filter(sale => 
            sale.payment_method && sale.payment_method.toLowerCase() === 'bank_transfer'
          );
          
          // Merge with existing bank transfers (avoid duplicates)
          salesBankTransfers.forEach(sale => {
            if (!bankTransfersReceived.find(p => p.id === sale.id)) {
              bankTransfersReceived.push(sale);
            }
          });
        } else {
        }
      } catch (error) {
        console.error('Error fetching payments/sales for bank transfers:', error);
      }
      
      // Also check customer payments for bank transfers
      try {
        const customerPaymentsResponse = await apiService.getCustomerPaymentsCashBreakdown(currentDate);
        if (customerPaymentsResponse.success && customerPaymentsResponse.data) {
          // This might contain bank transfer data - need to check the structure
        }
      } catch (error) {
        console.error('Error fetching customer payments for bank transfers:', error);
      }
      
      // Fetch bank transfer expenses today
      const expenseResponse = await apiService.getExpenses({ per_page: 1000 });
      const allExpenses = expenseResponse.data || [];
      
      const bankTransferExpenses = allExpenses.filter(expense => {
        const expenseDate = expense.expense_date.includes('T') 
          ? expense.expense_date.split('T')[0] 
          : expense.expense_date;
        const isToday = expenseDate === currentDate;
        const isBankTransfer = expense.mode_of_payment && expense.mode_of_payment.toLowerCase() === 'bank_transfer';
        
        return isToday && isBankTransfer;
      });
      
      // Also check supplier payments for bank transfers
      let bankTransferSupplierPayments = [];
      try {
        const supplierPaymentsResponse = await apiService.getSupplierPayments({ per_page: 1000 });
        if (supplierPaymentsResponse.success && supplierPaymentsResponse.data && Array.isArray(supplierPaymentsResponse.data)) {
          bankTransferSupplierPayments = supplierPaymentsResponse.data.filter(payment => {
            const paymentDate = payment.payment_date && payment.payment_date.includes('T') 
              ? payment.payment_date.split('T')[0] 
              : (payment.created_at && payment.created_at.includes('T') 
                ? payment.created_at.split('T')[0] 
                : payment.payment_date || payment.created_at);
            const isToday = paymentDate === currentDate;
            const isBankTransfer = payment.payment_method && payment.payment_method.toLowerCase() === 'bank_transfer';
            
            return isToday && isBankTransfer;
          });
        }
      } catch (error) {
        console.error('Error fetching supplier payments for bank transfers:', error);
      }
      
      const receivedTotal = bankTransfersReceived.reduce((sum, transfer) => sum + (parseFloat(transfer.amount) || 0), 0);
      const paidTotal = bankTransferExpenses.reduce((sum, expense) => sum + (parseFloat(expense.amount) || 0), 0) + 
                       bankTransferSupplierPayments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
      
      // Use the existing bankLodgement data that's already filtered by date
      const bankLodgementsTotal = calculateTotalValue(bankLodgement);
      const bankLodgementsCount = Object.values(bankLodgement).filter(value => value > 0).length;
      
      setBankTransfers({
        received: {
          total_amount: receivedTotal,
          transaction_count: bankTransfersReceived.length,
          transactions: bankTransfersReceived
        },
        paid: {
          total_amount: paidTotal,
          transaction_count: bankTransferExpenses.length + bankTransferSupplierPayments.length,
          transactions: [...bankTransferExpenses, ...bankTransferSupplierPayments]
        },
        lodgements: {
          total_amount: bankLodgementsTotal,
          transaction_count: bankLodgementsCount,
          transactions: [] // Could be enhanced to track individual lodgement entries
        },
        previous_balance: previousBankBalance
      });
    } catch (error) {
      console.error('Error fetching bank transfers:', error);
      // Silent error - no toast notification
    }
  }, [currentDate, bankLodgement]);


  // Fetch customer payments cash breakdown
  const fetchCustomerPaymentsCashBreakdown = useCallback(async () => {
    try {
      const response = await apiService.getCustomerPaymentsCashBreakdown(currentDate);
      if (response.success) {
        // Get the cash breakdown or default to zeros
        const defaultDenoms = { 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0 };
        const cashBreakdown = response.data.cash_breakdown || defaultDenoms;
        
        // Only update if data actually changed
        setTotalCashReceived(prev => {
          if (deepEqual(prev, cashBreakdown)) {
            return prev; // Return same reference to prevent re-render
          }
          return cashBreakdown;
        });
      }
    } catch (error) {
      console.error('Error fetching customer payments cash breakdown:', error);
      // On error, reset to zeros to prevent stale data
      const defaultDenoms = { 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0 };
      setTotalCashReceived(prev => {
        if (deepEqual(prev, defaultDenoms)) {
          return prev;
        }
        return defaultDenoms;
      });
    }
  }, [currentDate, deepEqual]);

  // Fetch supplier payments and cash expenses breakdown
  const fetchSupplierPaymentsCashBreakdown = useCallback(async () => {
    try {
      const defaultDenoms = { 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0 };
      
      // Fetch supplier payments
      const supplierResponse = await apiService.getSupplierPaymentsCashBreakdown(currentDate);
      let supplierBreakdown = { ...defaultDenoms };
      if (supplierResponse.success && supplierResponse.data.cash_breakdown) {
        supplierBreakdown = { ...defaultDenoms, ...supplierResponse.data.cash_breakdown };
      }

      // Fetch cash expenses filtered by date
      const expenseResponse = await apiService.getExpenses({ 
        per_page: 1000,
        date_from: currentDate,
        date_to: currentDate,
        payment_method: 'cash'
      });
      const todayExpenses = expenseResponse.data || [];

      // Combine supplier payments and cash expenses
      const combinedBreakdown = { ...supplierBreakdown };
      
      todayExpenses.forEach(expense => {
        if (expense.cash_breakdown) {
          Object.entries(expense.cash_breakdown).forEach(([denomination, quantity]) => {
            const denom = parseFloat(denomination);
            if (!isNaN(denom)) {
              combinedBreakdown[denom] = (combinedBreakdown[denom] || 0) + (parseInt(quantity) || 0);
            }
          });
        }
      });

      // Only update if data actually changed
      setTotalCashPayments(prev => {
        if (deepEqual(prev, combinedBreakdown)) {
          return prev; // Return same reference to prevent re-render
        }
        return combinedBreakdown;
      });
    } catch (error) {
      console.error('Error fetching supplier payments and cash expenses breakdown:', error);
      // On error, reset to zeros to prevent stale data
      const defaultDenoms = { 200: 0, 100: 0, 50: 0, 20: 0, 10: 0, 5: 0, 2: 0, 1: 0, 0.5: 0, 0.2: 0, 0.1: 0, 0.05: 0 };
      setTotalCashPayments(prev => {
        if (deepEqual(prev, defaultDenoms)) {
          return prev;
        }
        return defaultDenoms;
      });
    }
  }, [currentDate, deepEqual]);

  // Check and set previous balance from yesterday's closing balance
  const checkAndSetPreviousBalance = useCallback(async () => {
    try {
      // Get yesterday's date
      const currentDateObj = new Date(currentDate);
      const yesterday = new Date(currentDateObj);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      // Fetch yesterday's data
      const response = await apiService.getCashAnalysis(yesterdayStr);
      
      if (response.success && response.data.denominations) {
        const yesterdayDenoms = response.data.denominations;
        
        // Calculate yesterday's total cash available
        const yesterdayTotalCashAvailable = calculateYesterdayTotalCashAvailable(yesterdayDenoms);
        
        // Check if today's previous balance is empty or zero
        const currentPreviousBalance = calculateTotalValue(previousBalance);
        
        // If current previous balance is zero or very small, set it to yesterday's closing
        if (currentPreviousBalance < 1) {
          // Set today's previous balance to yesterday's total cash available
          const promises = Object.entries(yesterdayTotalCashAvailable).map(([denomination, quantity]) =>
            apiService.updateCashDenomination({
              date: currentDate,
              type: 'previous_balance',
              value: denomination,
              quantity: quantity
            })
          );
          
          await Promise.all(promises);
          
          // Refresh today's data to show the updated previous balance
          await fetchCashAnalysis(currentDate);
        }
      }
    } catch (error) {
      console.error('Error setting previous balance:', error);
      // Silent error - don't show toast
    }
  }, [currentDate, previousBalance]);

  // Calculate yesterday's total cash available
  const calculateYesterdayTotalCashAvailable = (yesterdayDenoms) => {
    const netFlow = {};
    denominations.forEach(denomination => {
      const totalCashReceived = yesterdayDenoms.total_cash_received?.[denomination] || 0;
      const salePos = yesterdayDenoms.sale_pos?.[denomination] || 0;
      const additionalCash = yesterdayDenoms.additional_cash?.[denomination] || 0;
      const convertEcashToCash = yesterdayDenoms.convert_ecash_to_cash?.[denomination] || 0;
      const totalCashProceeds = salePos + additionalCash + convertEcashToCash + totalCashReceived;
      const totalCashPayments = yesterdayDenoms.total_cash_payments?.[denomination] || 0;
      const convertCashToEcash = yesterdayDenoms.convert_cash_to_ecash?.[denomination] || 0;
      
      netFlow[denomination] = totalCashReceived + totalCashProceeds - totalCashPayments - convertCashToEcash;
    });
    
    const available = {};
    denominations.forEach(denomination => {
      const bankLodgement = yesterdayDenoms.bank_lodgement?.[denomination] || 0;
      const prevBalance = yesterdayDenoms.previous_balance?.[denomination] || 0;
      available[denomination] = (netFlow[denomination] || 0) - bankLodgement + prevBalance;
    });
    
    return available;
  };

  const saveCashAnalysis = useCallback(async () => {
    try {
      
      // Since this is now read-only, we'll just refresh the data
      await fetchCashAnalysis(currentDate);
      await fetchMobileMoneyBalance(); // Also refresh mobile money balance
      await fetchCustomerPaymentsCashBreakdown(); // Refresh customer payments cash breakdown
      await fetchSupplierPaymentsCashBreakdown(); // Refresh supplier payments cash breakdown
      
    } catch (error) {
      console.error('Error refreshing cash analysis:', error);
      // Silent error - no toast notification
    }
  }, [currentDate, fetchCashAnalysis, fetchMobileMoneyBalance, fetchCustomerPaymentsCashBreakdown, fetchSupplierPaymentsCashBreakdown]);


  // Load data on component mount and date change
  useEffect(() => {
    fetchCashAnalysis(currentDate);
    // fetchMobileMoneyBalance(); // Disabled - mobile money account not configured
    fetchMobileMoneySales(); // Fetch mobile money sales
    fetchMobileMoneyExpenses(); // Fetch mobile money expenses
    fetchBankTransfers(); // Fetch bank transfer data
    fetchCustomerPaymentsCashBreakdown(); // Fetch customer payments cash breakdown
    fetchSupplierPaymentsCashBreakdown(); // Fetch supplier payments cash breakdown
    checkAndSetPreviousBalance(); // Check if we need to set previous balance from yesterday
  }, [currentDate]); // Only when date changes, not when fetchCashAnalysis changes

  // Auto-refresh data every 3 seconds for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchCashAnalysis(currentDate);
      // fetchMobileMoneyBalance(); // Disabled - mobile money account not configured
      fetchMobileMoneySales();
      fetchMobileMoneyExpenses();
      fetchBankTransfers();
      fetchCustomerPaymentsCashBreakdown();
      fetchSupplierPaymentsCashBreakdown();
    }, 3000); // Refresh every 3 seconds for near real-time updates

    return () => clearInterval(interval);
  }, [currentDate, fetchCashAnalysis, fetchMobileMoneySales, fetchMobileMoneyExpenses, fetchBankTransfers, fetchCustomerPaymentsCashBreakdown, fetchSupplierPaymentsCashBreakdown]);

  // Calculate Total Cash Proceeds: Sale / POS + Additional Cash + Convert E-Cash to Cash + Total Cash Received
  const calculateTotalCashProceeds = useMemo(() => {
    const proceeds = {};
    denominations.forEach(denomination => {
      proceeds[denomination] = 
        (salePos[denomination] || 0) +
        (additionalCash[denomination] || 0) +
        (convertEcashToCash[denomination] || 0) +
        (totalCashReceived[denomination] || 0);
    });
    return proceeds;
  }, [salePos, additionalCash, convertEcashToCash, totalCashReceived]);

  // Update totalCashProceeds state with calculated values
  React.useEffect(() => {
    setTotalCashProceeds(calculateTotalCashProceeds);
  }, [calculateTotalCashProceeds]);

  // Calculate Net Cash Flow for Today: Total Cash Received + Total Cash Proceeds - Total Cash Payments - Convert Cash to E-Cash
  const calculateNetCashFlow = useMemo(() => {
    const netFlow = {};
    denominations.forEach(denomination => {
      netFlow[denomination] = 
        (totalCashReceived[denomination] || 0) +
        (totalCashProceeds[denomination] || 0) -
        (totalCashPayments[denomination] || 0) -
        (convertCashToEcash[denomination] || 0);
    });
    return netFlow;
  }, [totalCashReceived, totalCashProceeds, totalCashPayments, convertCashToEcash]);

  // Calculate Total Cash Available: Net Cash Flow for Today - Bank Cash Lodgement + Previous Cash Balance
  const calculateTotalCashAvailable = useMemo(() => {
    const netFlow = calculateNetCashFlow;
    const available = {};
    denominations.forEach(denomination => {
      available[denomination] = 
        (netFlow[denomination] || 0) -
        (bankLodgement[denomination] || 0) +
        (previousBalance[denomination] || 0);
    });
    return available;
  }, [calculateNetCashFlow, bankLodgement, previousBalance]);

  // Update totalCashAvailable state with calculated values
  React.useEffect(() => {
    setTotalCashAvailable(calculateTotalCashAvailable);
  }, [calculateTotalCashAvailable]);

  // Auto-sync calculated totalCashAvailable to backend whenever it changes
  React.useEffect(() => {
    const syncTotalCashAvailable = async () => {
      try {
        const calculatedAvailable = calculateTotalCashAvailable;
        const promises = Object.entries(calculatedAvailable).map(([denomination, quantity]) => 
          apiService.updateCashDenomination({
            date: currentDate,
            type: 'total_cash_available',
            value: denomination,
            quantity: quantity
          })
        );
        await Promise.all(promises);
      } catch (error) {
        console.error('Error syncing total cash available:', error);
      }
    };

    // Only sync if we have analysis data loaded (to avoid syncing during initial load)
    if (analysisData && Object.keys(calculateTotalCashAvailable).length > 0) {
      syncTotalCashAvailable();
    }
  }, [calculateTotalCashAvailable, currentDate, analysisData]);

  // Calculate net cash flow dynamically (for display purposes)
  const netCashFlow = calculateTotalValue(calculateNetCashFlow);



  // Cash table component with optional interactive mode - Memoized to prevent unnecessary re-renders
  const CashTable = React.memo(({ title, data, setData, bgColor = "bg-orange-50", interactive = false }) => {
    const handleQuantityChange = useCallback((denomination, e) => {
      if (interactive && setData) {
        const value = e.target.value;
        setData(prev => ({
          ...prev,
          [denomination]: value
        }));
      }
    }, [interactive, setData]);

    // Memoize the total calculation to prevent recalculation on every render
    const totalValue = useMemo(() => calculateTotalValue(data), [data]);

    // Memoize the rendered rows to prevent visual flickering
    const renderedRows = useMemo(() => 
      denominations.map(denomination => {
        const isCoin = denomination < 1;
        const displayValue = isCoin ? denomination.toFixed(2) : denomination;
        const quantity = data[denomination] || 0;
        const totalValue = (parseFloat(quantity) || 0) * denomination;
        
        return (
          <div key={denomination} className="grid grid-cols-3 gap-2 sm:gap-3 items-center py-1">
            <div className="text-xs sm:text-sm font-semibold text-gray-700 text-center">
              Ghs {displayValue}
              {isCoin && <span className="block text-xs text-gray-500">coin</span>}
            </div>
            {interactive ? (
              <input
                type="text"
                value={quantity || ''}
                onChange={(e) => handleQuantityChange(denomination, e)}
                className="w-full px-1 sm:px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 bg-white"
              />
            ) : (
              <div className="text-xs sm:text-sm font-medium text-gray-800 text-center">{quantity}</div>
            )}
            <div className="text-xs font-bold text-green-600 text-center">
              {formatCurrency(totalValue)}
            </div>
          </div>
        );
      }), [data, interactive, handleQuantityChange]);

    return (
      <div className={`${bgColor} rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-white/20 shadow-xl backdrop-blur-sm transition-all duration-300 hover:shadow-2xl hover:scale-105`}>
        <h4 className="font-bold text-gray-800 mb-3 sm:mb-4 text-center text-sm sm:text-base lg:text-lg">{title}</h4>
        <div className="space-y-2 sm:space-y-3">
          <div className="grid grid-cols-3 gap-2 sm:gap-3 text-xs font-semibold text-gray-600 border-b border-gray-300 pb-1 sm:pb-2">
            <div className="text-center">Denomination</div>
            <div className="text-center">Quantity</div>
            <div className="text-center">Value</div>
          </div>
          {renderedRows}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 items-center pt-2 sm:pt-3 border-t-2 border-gray-300">
            <div className="font-bold text-gray-800 text-sm sm:text-base">Total</div>
            <div></div>
            <div className="font-bold text-xs sm:text-sm text-green-700 text-center">
              {formatCurrency(totalValue)}
            </div>
          </div>
        </div>
      </div>
    );
  });

  // Static cash table component (read-only) - Memoized to prevent unnecessary re-renders
  const StaticCashTable = React.memo(({ title, data, bgColor = "bg-orange-50", totalColor = "text-green-600" }) => {
    // Memoize the total calculation to prevent recalculation on every render
    const totalValue = useMemo(() => calculateTotalValue(data), [data]);

    // Memoize the rendered rows to prevent visual flickering
    const renderedRows = useMemo(() => 
      denominations.map(denomination => {
        const isCoin = denomination < 1;
        const displayValue = isCoin ? denomination.toFixed(2) : denomination;
        const quantity = data[denomination] || 0;
        const totalValue = (parseFloat(quantity) || 0) * denomination;
        
        return (
          <div key={denomination} className="grid grid-cols-3 gap-2 sm:gap-3 items-center py-1">
            <div className="text-xs sm:text-sm font-semibold text-gray-700 text-center">
              Ghs {displayValue}
              {isCoin && <span className="block text-xs text-gray-500">coin</span>}
            </div>
            <div className="text-xs sm:text-sm font-medium text-gray-800 text-center">{quantity}</div>
            <div className="text-xs font-bold text-green-600 text-center">
              {formatCurrency(totalValue)}
            </div>
          </div>
        );
      }), [data]);

    return (
      <div className={`${bgColor} rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-white/20 shadow-xl backdrop-blur-sm transition-all duration-300 hover:shadow-2xl hover:scale-105`}>
        <h4 className="font-bold text-gray-800 mb-3 sm:mb-4 text-center text-sm sm:text-base lg:text-lg">{title}</h4>
        <div className="space-y-2 sm:space-y-3">
          <div className="grid grid-cols-3 gap-2 sm:gap-3 text-xs font-semibold text-gray-600 border-b border-gray-300 pb-1 sm:pb-2">
            <div className="text-center">Denomination</div>
            <div className="text-center">Quantity</div>
            <div className="text-center">Value</div>
          </div>
          {renderedRows}
          <div className="grid grid-cols-3 gap-2 sm:gap-3 items-center pt-2 sm:pt-3 border-t-2 border-gray-300">
            <div className="font-bold text-gray-800 text-sm sm:text-base">Total</div>
            <div></div>
            <div className={`font-bold text-xs sm:text-sm ${totalColor} text-center`}>
              {formatCurrency(totalValue)}
            </div>
          </div>
        </div>
      </div>
    );
  });

  return (
    <Layout>
      <div className="space-y-6">

            {/* Page Header */}
            <div className="mb-6 sm:mb-8 lg:mb-10">
              <div className="flex flex-col gap-4 sm:gap-6">
                {/* Title and Date Row */}
                <div className="flex flex-col mt-16 sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="space-y-2">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent">
                      Daily Cash Analysis
                    </h1>
                    {/* <p className="text-gray-600 text-sm sm:text-base lg:text-lg">Comprehensive cash flow tracking and reconciliation dashboard</p> */}
                  </div>
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Calendar className="h-4 w-4 text-blue-500" />
                      </div>
                      <input
                        type="date"
                        value={currentDate}
                        onChange={(e) => setCurrentDate(e.target.value)}
                        className="w-full sm:w-auto pl-10 pr-4 py-3 border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm font-medium bg-gradient-to-r from-blue-50 to-indigo-50 shadow-lg hover:shadow-xl hover:from-blue-100 hover:to-indigo-100 transition-all duration-300 text-gray-700"
                      />
                    </div>
                  </div>
                  {/* Action Buttons Row */}
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 lg:gap-4">
                  <button
                    onClick={handleOpenTransactionModal}
                    className="inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
                  >
                    <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    <span className="font-medium">Add Transaction</span>
                  </button>
                  <button
                    onClick={handleOpenOpeningBalanceModal}
                    className="inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-sm sm:text-base"
                  >
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                    <span className="font-medium">Set Opening Balance</span>
                  </button>
                </div>
                </div>
                
                
              </div>
            </div>

            {/* Daily Cash Reconciliation Summary */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
              {/* Daily Cash Reconciliation */}
              <div className="bg-gradient-to-br from-white via-blue-50/30 to-indigo-50/50 rounded-xl p-4 shadow-lg border border-blue-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-2 shadow-sm">
                    <Calculator className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">Daily Cash Reconciliation</h3>
                </div>
                <div className="space-y-2">
                  {/* Today's Cash Sales */}
                  <div className="flex justify-between items-center px-2 py-1 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded border border-blue-200/50">
                    <span className="text-gray-700 text-xs font-medium">Today's Cash Sales:</span>
                    <span className="text-xs sm:text-sm font-bold text-blue-600">{formatCurrency(analysisData?.analysis?.total_cash_sales || 0)}</span>
                  </div>
                  
                  {/* Today's Mobile Money Sales */}
                  <div className="flex justify-between items-center px-2 py-1 bg-gradient-to-r from-purple-50 to-purple-100/50 rounded border border-purple-200/50">
                    <span className="text-gray-700 text-xs font-medium">Today's Mobile Money Sales:</span>
                    <span className="text-xs sm:text-sm font-bold text-purple-600">{formatCurrency(analysisData?.analysis?.momo_balance || 0)}</span>
                  </div>
                  
                  {/* Today's Mobile Money Expenses */}
                  <div className="flex justify-between items-center px-2 py-1 bg-gradient-to-r from-red-50 to-red-100/50 rounded border border-red-200/50">
                    <span className="text-gray-700 text-xs font-medium">Today's Mobile Money Expenses:</span>
                    <span className="text-xs sm:text-sm font-bold text-red-600">{formatCurrency(analysisData?.analysis?.total_mobile_money_expenses || 0)}</span>
                  </div>

                  {/* Today's Cash Expenses */}
                  <div className="flex justify-between items-center px-2 py-1 bg-gradient-to-r from-red-50 to-red-100/50 rounded border border-red-200/50">
                    <span className="text-gray-700 text-xs font-medium">Today's Cash Expenses:</span>
                    <span className="text-xs sm:text-sm font-bold text-red-600">{formatCurrency(analysisData?.analysis?.total_cash_expenses || 0)}</span>
                  </div>
                 
                  {/* Today's Total Cash Proceeds */}
                  <div className="flex justify-between items-center px-2 py-2 bg-gradient-to-r from-blue-600 to-indigo-700 rounded shadow-sm">
                    <span className="text-sm font-bold text-white">TODAY'S TOTAL PROCEEDS:</span>
                    <span className="text-sm sm:text-base font-bold text-white">{formatCurrency((analysisData?.analysis?.total_cash_sales || 0) + (analysisData?.analysis?.momo_balance || 0))}</span>
                  </div>
                  
                </div>
              </div>

              {/* General Summary */}
              <div className="bg-gradient-to-br from-white via-emerald-50/30 to-green-50/50 rounded-xl p-4 shadow-lg border border-emerald-100/50 backdrop-blur-sm">
                <div className="flex items-center mb-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-green-600 rounded-lg flex items-center justify-center mr-2 shadow-sm">
                    <DollarSign className="w-4 h-4 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-gray-900">General Summary (Cumulative)</h3>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between items-center px-2 py-1 bg-gradient-to-r from-purple-50 to-purple-100/50 rounded border border-purple-200/50">
                    <span className="text-gray-700 text-xs font-medium">Total Momo Balance:</span>
                    <span className="text-xs sm:text-sm font-bold text-purple-600">{formatCurrency(analysisData?.analysis?.total_momo_balance || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center px-2 py-1 bg-gradient-to-r from-blue-50 to-blue-100/50 rounded border border-blue-200/50">
                    <span className="text-gray-700 text-xs font-medium">Total Cash Available:</span>
                    <span className="text-xs sm:text-sm font-bold text-blue-600">{formatCurrency(analysisData?.analysis?.total_cash_available || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center px-2 py-1 bg-gradient-to-r from-indigo-50 to-indigo-100/50 rounded border border-indigo-200/50">
                    <span className="text-gray-700 text-xs font-medium">Cash + Momo Sub Total:</span>
                    <span className="text-xs sm:text-sm font-bold text-indigo-600">{formatCurrency(analysisData?.analysis?.cash_momo_subtotal || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center px-2 py-1 bg-gradient-to-r from-cyan-50 to-cyan-100/50 rounded border border-cyan-200/50">
                    <span className="text-gray-700 text-xs font-medium">Total Bank Balance:</span>
                    <span className="text-xs sm:text-sm font-bold text-cyan-600">{formatCurrency(analysisData?.analysis?.bank_balance || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center px-2 py-2 bg-gradient-to-r from-emerald-600 to-green-700 rounded shadow-sm">
                    <span className="text-sm font-bold text-white">GRAND TOTAL:</span>
                    <span className="text-sm sm:text-base font-bold text-white">{formatCurrency(analysisData?.analysis?.grand_total || 0)}</span>
                  </div>
                </div>
              </div>
            </div>


            {/* Cash Tracking Tables - Row 1: Opening Balances & Cash Sources */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <StaticCashTable title={`Previous Cash Balance (${getPreviousBusinessDay()})`} data={previousBalance} bgColor="bg-gradient-to-br from-blue-50 to-blue-100" totalColor="text-blue-700" />
              <CashTable 
                title="Sale / POS" 
                data={salePos} 
                bgColor="bg-gradient-to-br from-orange-50 to-orange-100" 
                interactive={false}
              />
              <CashTable 
                title="Additional Cash" 
                data={additionalCash} 
                bgColor="bg-gradient-to-br from-orange-50 to-orange-100" 
              />
              <CashTable 
                title="Cash Conversion" 
                data={cashConversion} 
                bgColor="bg-gradient-to-br from-orange-50 to-orange-100" 
              />
            </div>

            {/* Cash Tracking Tables - Row 2: Cash Conversions & Transfers */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <CashTable 
                title="Convert E-Cash to Cash" 
                data={convertEcashToCash} 
                bgColor="bg-gradient-to-br from-yellow-50 to-yellow-100" 
              />
              <CashTable 
                title="Convert Cash to E-Cash" 
                data={convertCashToEcash} 
                bgColor="bg-gradient-to-br from-yellow-50 to-yellow-100" 
              />
              <StaticCashTable title="Bank Cash Lodgement" data={bankLodgement} bgColor="bg-gradient-to-br from-yellow-50 to-yellow-100" totalColor="text-green-700" />
              <StaticCashTable title="Total Cash Received" data={totalCashReceived} bgColor="bg-gradient-to-br from-green-50 to-green-100" totalColor="text-green-700" />
            </div>

            {/* Cash Tracking Tables - Row 3: Cash Outflows & Payments */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
              <StaticCashTable title="Total Cash Payments" data={totalCashPayments} bgColor="bg-gradient-to-br from-red-50 to-red-100" totalColor="text-red-700" />
              <StaticCashTable title="Total Cash Proceeds" data={totalCashProceeds} bgColor="bg-gradient-to-br from-green-50 to-green-100" totalColor="text-green-700" />
              <StaticCashTable 
                title="Net Cash Flow for Today" 
                data={calculateNetCashFlow} 
                bgColor="bg-gradient-to-br from-purple-50 to-purple-100" 
                totalColor="text-purple-700"
              />
              <StaticCashTable title="Total Cash Available" data={totalCashAvailable} bgColor="bg-gradient-to-br from-green-50 to-green-100" totalColor="text-green-700" />
            </div>

      </div>

      {/* Transaction Modal */}
      {showTransactionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Header with gradient background */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">Add Cash Transaction</h3>
                  <p className="text-blue-100 text-xs sm:text-sm mt-1">Record cash denominations for selected card type</p>
                </div>
                <button
                  onClick={handleCloseTransactionModal}
                  className="text-white hover:text-blue-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Card Type Selection */}
              <div className="bg-gray-50 rounded-xl p-4">
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                    Select Card Type
                  </span>
                </label>
                <select
                  value={transactionForm.cardType}
                  onChange={(e) => handleTransactionFormChange('cardType', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white font-medium transition-all duration-200"
                >
                  <option value="">Choose a card type...</option>
                  <option value="sale_pos">Sale / POS</option>
                  <option value="additional_cash">Additional Cash</option>
                  <option value="cash_conversion">Cash Conversion</option>
                  <option value="convert_ecash_to_cash">Convert E-Cash to Cash</option>
                  <option value="convert_cash_to_ecash">Convert Cash to E-Cash</option>
                  <option value="bank_lodgement">Bank Lodgement</option>
                  <option value="total_cash_received">Total Cash Received</option>
                </select>
              </div>

              {/* Denominations Input */}
              {transactionForm.cardType && (
                <div className="bg-white border-2 border-gray-100 rounded-xl p-5">
                  <label className="block text-sm font-semibold text-gray-800 mb-4">
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                      Enter Quantities for Each Denomination
                    </span>
                    <span className="text-xs text-gray-500 mt-1 block">Negative values allowed for adjustments</span>
                  </label>
                  <div className="space-y-4">
                    {denominations.map(denomination => {
                      const isCoin = denomination < 1;
                      const displayValue = isCoin ? denomination.toFixed(2) : denomination;
                      return (
                        <div key={denomination} className="grid grid-cols-3 gap-4 items-center p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm font-bold text-gray-800 bg-white px-3 py-2 rounded-lg text-center">
                            Ghs {displayValue}
                            {isCoin && <span className="block text-xs text-gray-500">coin</span>}
                          </div>
                          <input
                            type="number"
                            value={transactionForm.denominations[denomination] || ''}
                            onChange={(e) => handleDenominationChange(denomination, e.target.value)}
                            className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-medium text-center transition-all duration-200"
                            placeholder="0"
                          />
                          <div className={`text-xs font-bold px-2 py-1 rounded-lg text-center ${((transactionForm.denominations[denomination] || 0) * denomination) < 0 ? 'text-red-600 bg-red-50' : 'text-green-600 bg-green-50'}`}>
                            {formatCurrency((transactionForm.denominations[denomination] || 0) * denomination)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Total */}
                  <div className="mt-6 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-100">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-800">Transaction Total:</span>
                      <span className={`text-lg sm:text-xl font-bold px-3 py-2 rounded-lg ${(() => {
                        const total = Object.entries(transactionForm.denominations).reduce((total, [denomination, quantity]) => {
                          return total + (parseFloat(denomination) * (parseFloat(quantity) || 0));
                        }, 0);
                        return total < 0 ? 'text-red-600 bg-red-100' : 'text-green-600 bg-green-100';
                      })()}`}>
                        {formatCurrency(Object.entries(transactionForm.denominations).reduce((total, [denomination, quantity]) => {
                          return total + (parseFloat(denomination) * (parseFloat(quantity) || 0));
                        }, 0))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer with improved buttons */}
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl sm:rounded-b-2xl">
              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
                <button
                  onClick={handleCloseTransactionModal}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 font-medium text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTransaction}
                  disabled={!transactionForm.cardType}
                  className="w-full sm:w-auto px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none text-sm sm:text-base"
                >
                  Save Transaction
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Opening Balance Modal */}
      {showOpeningBalanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            {/* Header with gradient background */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-white">Set Opening Balance</h3>
                  <p className="text-purple-100 text-xs sm:text-sm mt-1">Set the previous day's cash balance for {getPreviousBusinessDay()}</p>
                </div>
                <button
                  onClick={handleCloseOpeningBalanceModal}
                  className="text-white hover:text-purple-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Opening Balance Input */}
              <div className="bg-white border-2 border-gray-100 rounded-xl p-5">
                <label className="block text-sm font-semibold text-gray-800 mb-4">
                  <span className="flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    Enter Opening Cash Balance
                  </span>
                  <span className="text-xs text-gray-500 mt-1 block">Set the cash denominations for the previous business day</span>
                </label>
                <div className="space-y-4">
                  {denominations.map(denomination => {
                    const isCoin = denomination < 1;
                    const displayValue = isCoin ? denomination.toFixed(2) : denomination;
                    return (
                      <div key={denomination} className="grid grid-cols-3 gap-4 items-center p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm font-bold text-gray-800 bg-white px-3 py-2 rounded-lg text-center">
                          Ghs {displayValue}
                          {isCoin && <span className="block text-xs text-gray-500">coin</span>}
                        </div>
                        <input
                          type="number"
                          value={openingBalanceForm.denominations[denomination] || ''}
                          onChange={(e) => handleOpeningBalanceDenominationChange(denomination, e.target.value)}
                          className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-medium text-center transition-all duration-200"
                          placeholder="0"
                        />
                        <div className="text-xs font-bold px-2 py-1 rounded-lg text-center text-green-600 bg-green-50">
                          {formatCurrency((openingBalanceForm.denominations[denomination] || 0) * denomination)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Total */}
                <div className="mt-6 p-5 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl border-2 border-purple-100">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-800">Opening Balance Total:</span>
                    <span className="text-lg sm:text-xl font-bold px-3 py-2 rounded-lg text-purple-600 bg-purple-100">
                      {formatCurrency(Object.entries(openingBalanceForm.denominations).reduce((total, [denomination, quantity]) => {
                        return total + (parseFloat(denomination) * (parseFloat(quantity) || 0));
                      }, 0))}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with improved buttons */}
            <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl sm:rounded-b-2xl">
              <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
                <button
                  onClick={handleCloseOpeningBalanceModal}
                  className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 font-medium text-sm sm:text-base"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOpeningBalance}
                  className="w-full sm:w-auto px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl hover:from-purple-700 hover:to-purple-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl text-sm sm:text-base"
                >
                  Set Opening Balance
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default CashAnalysis;

